import type { Camera, HistoryRecord, ChartDataPoint } from '@smolt/shared/types';

// ============================================================================
// Smolt Sorting/Counting Throughput Models
// ============================================================================

export interface ThroughputModel {
  id: string;
  name: string;
  maxThroughputPerHour: number; // Fish per hour
  description: string;
}

export const THROUGHPUT_MODELS: Record<string, ThroughputModel> = {
  VAKI_SMOLT: {
    id: 'vaki_smolt',
    name: 'VAKI Smolt Counter',
    maxThroughputPerHour: 360000, // 100 fish/sec
    description: 'High capacity counter for continuous flow',
  },
  VAKI_NANO: {
    id: 'vaki_nano',
    name: 'VAKI Nano Counter',
    maxThroughputPerHour: 200000, // ~55 fish/sec
    description: 'Optimized for smaller fish (1g)',
  },
  AQUASCAN: {
    id: 'aquascan',
    name: 'AquaScan CSW',
    maxThroughputPerHour: 1000000, // ~277 fish/sec
    description: 'High volume counter with wide sensor',
  },
  AQUATICODE: {
    id: 'aquaticode',
    name: 'Aquaticode SORTpro',
    maxThroughputPerHour: 10000, // ~2.7 fish/sec (grading/sorting)
    description: 'High accuracy sorting/grading',
  },
};

// ============================================================================
// Session Profiles (Duration & Context)
// ============================================================================

export interface SessionProfile {
  id: string;
  name: string;
  durationRange: [number, number]; // Seconds [min, max]
  typicalCountRange: [number, number];
  probability: number; // 0-1
}

export const SESSION_PROFILES: Record<string, SessionProfile> = {
  TEST: {
    id: 'test',
    name: 'Test/Sampling',
    durationRange: [300, 900], // 5-15 min
    typicalCountRange: [1000, 10000],
    probability: 0.15,
  },
  GRADING: {
    id: 'grading',
    name: 'Grading Batch',
    durationRange: [1800, 7200], // 30 min - 2 hours
    typicalCountRange: [50000, 200000],
    probability: 0.5,
  },
  TRANSFER: {
    id: 'transfer',
    name: 'Large Transfer',
    durationRange: [7200, 21600], // 2 - 6 hours
    typicalCountRange: [200000, 500000],
    probability: 0.35,
  },
};

// ============================================================================
// Realistic Data Generator
// ============================================================================

interface GenerationParams {
  durationSeconds: number;
  targetCount: number;
  model?: ThroughputModel;
}

/**
 * Generate chart data with consistent linear progression.
 *
 * No ramp-up/wind-down phases - starts at full rate immediately.
 * Small random variation (±10%) for natural appearance.
 */
export const generateRealisticChartData = (params: GenerationParams): ChartDataPoint[] => {
  const { durationSeconds, targetCount } = params;

  if (durationSeconds <= 0 || targetCount <= 0) {
    return [{ time: '00:00', count: 0 }];
  }

  // Sampling interval configuration
  const interval = durationSeconds <= 600 ? 1 : durationSeconds <= 3600 ? 5 : 10;
  const numPoints = Math.floor(durationSeconds / interval) + 1;

  const data: ChartDataPoint[] = [];
  let currentCount = 0;

  for (let i = 0; i < numPoints; i++) {
    const elapsed = i * interval;
    const minutes = Math.floor(elapsed / 60);
    const secs = elapsed % 60;

    // Calculate expected count at this time (linear progression)
    const expectedCount = (elapsed / durationSeconds) * targetCount;

    // Add small random variation (±5%) for natural appearance
    const variation = 0.95 + Math.random() * 0.1;
    currentCount = expectedCount * variation;

    // Ensure monotonic increase and don't exceed target
    if (i > 0 && currentCount < data[i - 1].count) {
      currentCount = data[i - 1].count;
    }
    if (currentCount > targetCount) {
      currentCount = targetCount;
    }

    data.push({
      time: `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
      count: Math.floor(currentCount),
    });
  }

  // Ensure final point is exactly the target
  if (data.length > 0) {
    data[data.length - 1].count = targetCount;
  }

  return data;
};

// ============================================================================
// History Generation
// ============================================================================

const historyChartDataCache = new Map<string, ChartDataPoint[]>();

// Tank pools for In/Out columns
const INPUT_TANKS = ['A-01', 'A-02', 'A-03', 'B-01', 'B-02', 'B-03', 'B-04', 'B-05'];
const OUTPUT_TANKS = ['C-01', 'C-02', 'C-03', 'D-01', 'D-02', 'D-03', 'D-04'];

export const generateHistoryRecords = (count = 50): HistoryRecord[] => {
  const records: HistoryRecord[] = [];
  const now = new Date();

  // Sort profiles by probability for random selection
  const profiles = Object.values(SESSION_PROFILES);

  for (let i = 0; i < count; i++) {
    // 1. Select Profile
    const rand = Math.random();
    let cumulativeProb = 0;
    let selectedProfile = profiles[0];

    for (const profile of profiles) {
      cumulativeProb += profile.probability;
      if (rand <= cumulativeProb) {
        selectedProfile = profile;
        break;
      }
    }

    // 2. Generate Parameters
    const durationSeconds = Math.floor(
      selectedProfile.durationRange[0] +
        Math.random() * (selectedProfile.durationRange[1] - selectedProfile.durationRange[0])
    );

    const targetCount = Math.floor(
      selectedProfile.typicalCountRange[0] +
        Math.random() *
          (selectedProfile.typicalCountRange[1] - selectedProfile.typicalCountRange[0])
    );

    // 3. Generate Time
    // Spread records over last 30 days
    const daysAgo = Math.floor((i / count) * 30);
    const recordTime = new Date(now.getTime() - daysAgo * 86400000 - Math.random() * 36000000);

    const year = recordTime.getFullYear();
    const month = String(recordTime.getMonth() + 1).padStart(2, '0');
    const day = String(recordTime.getDate()).padStart(2, '0');

    // 4. Select tanks (random input and output)
    const inputTank = INPUT_TANKS[Math.floor(Math.random() * INPUT_TANKS.length)];
    const outputTank = OUTPUT_TANKS[Math.floor(Math.random() * OUTPUT_TANKS.length)];

    // 5. Calculate Stats
    const kgPerFish = 0.1 + Math.random() * 4.9; // 100g to 5kg
    const weight = (targetCount * kgPerFish) / 1000; // Tons

    const durationHours = Math.floor(durationSeconds / 3600);
    const durationMinutes = Math.floor((durationSeconds % 3600) / 60);
    const durationSecs = durationSeconds % 60;

    const recordKey = `record-${i}`;

    // 6. Generate Chart Data
    const chartData = generateRealisticChartData({
      durationSeconds,
      targetCount,
    });

    historyChartDataCache.set(recordKey, chartData);

    records.push({
      key: recordKey,
      time: `${year}-${month}-${day}`,
      inputTank,
      outputTank,
      duration: `${String(durationHours).padStart(2, '0')}:${String(durationMinutes).padStart(2, '0')}:${String(durationSecs).padStart(2, '0')}`,
      count: targetCount,
      weight: parseFloat(weight.toFixed(2)),
      kgPerFish: parseFloat(kgPerFish.toFixed(2)),
    });
  }

  return records.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
};

export const getHistoryChartData = (recordKey: string): ChartDataPoint[] => {
  return historyChartDataCache.get(recordKey) || [];
};

export const saveHistoryChartData = (recordKey: string, data: ChartDataPoint[]): void => {
  historyChartDataCache.set(recordKey, data);
};

// ============================================================================
// Mock Cameras (Preserved)
// ============================================================================

export const mockCameras: Camera[] = [
  {
    id: 'cam-01',
    name: 'CAMERA-01',
    cameraState: 'playing',
    sessionState: 'counting',
    count: 136000,
    weight: 197.2,
    duration: '01:20:00',
    weightPerFish: 1.45,
    inputTank: 'B-05',
    outputTank: 'C-01',
  },
  {
    id: 'cam-02',
    name: 'CAMERA-02',
    cameraState: 'playing',
    sessionState: 'ready',
    count: 0,
    weight: 0,
    duration: '00:00:00',
  },
  {
    id: 'cam-03',
    name: 'CAMERA-03',
    cameraState: 'playing',
    sessionState: 'ready',
    count: 0,
    weight: 0,
    duration: '00:00:00',
  },
  {
    id: 'cam-04',
    name: 'CAMERA-04',
    cameraState: 'playing',
    sessionState: 'ready',
    count: 0,
    weight: 0,
    duration: '00:00:00',
  },
];

// Re-export for compatibility if needed
export const generateChartData = (
  durationSeconds: number,
  targetCount: number
): ChartDataPoint[] => {
  return generateRealisticChartData({ durationSeconds, targetCount });
};

/**
 * Generate incremental chart data points for existing session.
 * Appends new data points to existing chart data without regenerating the entire chart.
 * This prevents visual jitter caused by random noise regeneration.
 */
export const generateIncrementalChartData = (
  existingData: ChartDataPoint[],
  fromSeconds: number,
  toSeconds: number,
  targetCount: number
): ChartDataPoint[] => {
  if (toSeconds <= fromSeconds) return [];

  const interval = toSeconds <= 600 ? 1 : toSeconds <= 3600 ? 5 : 10;
  const lastCount = existingData[existingData.length - 1]?.count || 0;
  const countDiff = targetCount - lastCount;
  const timeDiff = toSeconds - fromSeconds;

  if (timeDiff <= 0 || countDiff <= 0) return [];

  const newPoints: ChartDataPoint[] = [];
  let accumulatedCount = lastCount;

  // Calculate average rate for the period
  const avgRatePerSec = countDiff / timeDiff;

  for (let t = fromSeconds + interval; t <= toSeconds; t += interval) {
    // Add increment with small random variation (±10%)
    const variation = 0.9 + Math.random() * 0.2;
    const increment = avgRatePerSec * interval * variation;
    accumulatedCount += increment;

    // Ensure we don't exceed target
    if (accumulatedCount > targetCount) {
      accumulatedCount = targetCount;
    }

    const minutes = Math.floor(t / 60);
    const secs = t % 60;

    newPoints.push({
      time: `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
      count: Math.floor(accumulatedCount),
    });
  }

  return newPoints;
};
